import { useState, useRef, useContext } from "react";

import { Box, Toolbar, Grid, Container, Paper, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { ReloadContext } from "../context/ReloadContext";
import { Copyright } from "../components/Common/Copyright";
import KaishaSelect from "../components/Common/KaishaSelect";
import { KaishaBaitaiShosaiGrid } from "../components/KaishaBaitaiShosai/KaishaBaitaiShosaiGrid";
import {
  KaishaDataSet,
  KaishaBaitaiShosaiDataSet,
  KaishaBaitaiShosaiSearchParam,
} from "../types/WebData";
import { postInsertKaishaBaitaiShosai } from "../api/kaisha";
import { KENGEN } from "../const/index";
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * 会社媒体詳細紐付け設定画面コンポーネント
 *
 * @return {*}
 */
export const KaishaBaitaiShosai = () => {
  const isReadOnlyUser = useReadOnly();
  const reloadContext = useContext(ReloadContext);
  const [kaisha, setKaisha] = useState<KaishaDataSet | null>(null);
  const [gamenMei, setGamenMei] = useState<string>("");
  const [rows, setRows] = useState<KaishaBaitaiShosaiDataSet[] | null>(null);
  const [searchParam, setSearchParam] =
    useState<KaishaBaitaiShosaiSearchParam | null>(null);
  const [load, setLoad] = useState<boolean>(false);
  const [loadSave, setLoadSave] = useState<boolean>(false);

  // エラーメッセージ
  const [errorMessageMansion, setErrorMessageKaisha] = useState<string>("");

  // 処理中フラグ（ボタン連打対策）
  const processing = useRef(false);

  // 検索実行
  const handleSearch = async () => {
    setErrorMessageKaisha("");

    // 処理中(true)なら非同期処理せずに抜ける
    if (processing.current) return;
    // 処理中フラグを上げる
    processing.current = true;
    // 疑似非同期処理
    setTimeout(() => {
      // 処理中フラグを下げる
      processing.current = false;
    }, 1000);
    setLoad(true);

    // バリデーションチェック
    if (!kaisha?.id) {
      setLoad(false);
      return setErrorMessageKaisha("会社名は必須です。");
    }

    await setSearchParam({
      kaisha_id: kaisha ? kaisha.id : 0,
      gamen_mei: gamenMei,
    });
  };

  const handleSave = async () => {
    // 処理中(true)なら非同期処理せずに抜ける
    if (processing.current) return;
    // 処理中フラグを上げる
    processing.current = true;
    // 疑似非同期処理
    setTimeout(() => {
      // 処理中フラグを下げる
      processing.current = false;
    }, 1000);
    setLoadSave(true);

    // バリデーションチェック
    if (!kaisha?.id) {
      setLoadSave(false);
      return setErrorMessageKaisha("会社名は必須です。");
    }

    // 選択中の画面のみ送信する
    const saveRows = rows ? rows.filter((row) => row.is_selected) : null;
    const result = await postInsertKaishaBaitaiShosai(kaisha.id, saveRows);

    reloadContext?.setSnackbarInfo({
      isOpen: true,
      type: result.isError ? "error" : "success",
      message: result.message,
    });

    setLoadSave(false);
    //リロードする
    await reloadContext?.setReload(reloadContext?.reload + 1);
  };

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: "100%",
          overflow: "auto",
        }}
      >
        <Toolbar />

        <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
          <Grid container spacing={1}>
            {/* 検索 */}
            <Grid item xs={3}>
              <Paper
                sx={{
                  paddingY: 1,
                  paddingX: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: "75vh",
                }}
              >
                <Box>
                  <KaishaSelect
                    kaisha={kaisha}
                    setKaisha={setKaisha}
                    label="会社名 *"
                    kengenList={[KENGEN.DAIRITEN]}
                  />
                  {errorMessageMansion && (
                    <small className={"error-message"}>
                      {errorMessageMansion}
                    </small>
                  )}
                  <TextField
                    margin="normal"
                    fullWidth
                    label="画面名"
                    value={gamenMei}
                    onChange={(e) => setGamenMei(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                  <LoadingButton
                    type="button"
                    fullWidth
                    loading={load}
                    variant="contained"
                    sx={{ mt: 2, mb: 2 }}
                    onClick={() => handleSearch()}
                  >
                    検索
                  </LoadingButton>
                </Box>
              </Paper>
            </Grid>
            {/* 一覧 */}
            <Grid item xs={9}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "end",
                  flexDirection: "column",
                  height: "75vh",
                }}
              >
                {!isReadOnlyUser && (
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    onClick={handleSave}
                    loading={loadSave}
                    sx={{ mt: 0, mb: 2, maxWidth: "250px" }}
                  >
                    保存
                  </LoadingButton>
                )}
                <KaishaBaitaiShosaiGrid
                  searchParam={searchParam}
                  setload={setLoad}
                  setRows={setRows}
                />
              </Paper>
            </Grid>
          </Grid>
          <Copyright />
        </Container>
      </Box>
    </Box>
  );
};
